// Same as core.auth.auth.JWTClaim in Python code
import { SubState } from '../state';
import { AppState, GenericContent } from '../state.module';
import { InitializableState } from '../initializable-state';

export interface JWTClaim {
  sub: string;    // Subject; User ID
  exp: number;    // Expiry Timestamp
  iss: string;    // Issuer
  aud: string;    // Audience; Who the token is issued to
  iat: number;    // Issue Timestamp
  nonce: string;  // Client session ID
}

export class AuthState implements SubState, InitializableState {

  constructor(private _parent: AppState = null,
    private _initialized: boolean = false,
    private _userId: string = null,
    private _idToken: string = null,
    private _claim: JWTClaim = null,
    private _content: GenericContent = null,
    private _email: string = null,
    private _acl: string[] = [],
    private _userTypeId: string = null, ) {
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get parent(): AppState {
    return this._parent;
  }

  get userId(): string {
    return this._userId;
  }

  get userTypeId(): string {
    return this._userTypeId;
  }

  get idToken(): string {
    return this._idToken;
  }

  get claim(): JWTClaim {
    return this._claim;
  }

  get content(): GenericContent {
    return this._content;
  }

  get isAuthenticated(): boolean {
    return !!this._idToken;
  }

  get email(): string {
    return this._email;
  }

  get acl(): string[] {
    return Array.from(this._acl);
  }

  get isAdmin(): boolean {
    return this._acl.indexOf('adm') !== -1;
  }

  get isAgent(): boolean {
    return this._acl.indexOf('agn') !== -1;
  }

  get isMShp(): boolean {
    return this._acl.indexOf('mshp') !== -1;
  }
  get isCover(): boolean {
    return this._acl.indexOf('cov') !== -1;
  }
  get isShop(): boolean {
    return this._acl.indexOf('shp') !== -1;
  }

  get isLab(): boolean {
    return this._acl.indexOf('lab') !== -1;
  }

  get isClient(): boolean {
    return this._acl.indexOf('cli') !== -1;
  }

  get userPath() {
    switch (this._acl[0]) {
      case 'shp':
        return 'shop';
      case 'adm':
        return 'admin';
      case 'agn':
        return 'agent';
      case 'mshp':
        return 'mshp';
      case 'cov' :
        return 'cover';
      case 'cli':
        return 'client';
      case 'lab':
      default:
        return 'lab';
    }
  }

  login(userId: string,
    idToken: string,
    email: string,
    acl: string[],
    userTypeId: string): AppState {
    return this._parent.withAuth(
      new AuthState(
        this._parent,
        this._initialized,
        userId,
        idToken,
        this._claim,
        this._content,
        email,
        acl,
        userTypeId
      )
    );
  }

  logout(): AppState {
    return this._parent.withAuth(
      new AuthState(
        null,
        this._initialized,
        null,
        null,
        null,
        null,
        null,
        [],
        null
      )
    );
  }

  initialize(): AppState {
    return this._parent.withAuth(
      new AuthState(
        this._parent,
        true,
        this._userId,
        this._idToken,
        this._claim,
        this._content,
        this._email,
        this._acl,
        this._userTypeId,
      )
    );
  }

  withParent(parent: AppState): AuthState {
    return new AuthState(
      parent,
      this._initialized,
      this._userId,
      this._idToken,
      this._claim,
      this._content,
      this._email,
      this._acl,
      this._userTypeId,
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/state/auth/auth-state.ts